import * as mammoth from 'mammoth';
import { configurePdfjsWorker, pdfjsLib } from '../utils/pdfjsSetup';
import {
  ExtractedDocument,
  ExtractedPage,
  ExtractedTable,
  ExtractedTableCell,
  ExtractedTextBlock,
  SourceFormat
} from '../types/extraction';
import { parseRobustRtf } from '../utils/robustRtfParser';
import { rtfRowsToExtractedTables } from '../services/xltekTemplateParser';

const TEXT_FORMATS = new Set(['txt', 'csv']);

export class StructuredDocumentExtractor {
  static async extract(file: File): Promise<ExtractedDocument> {
    const format = this.getFormat(file);

    if (format === 'docx' || format === 'doc') {
      return this.extractDocx(file, format);
    }

    if (format === 'pdf') {
      return this.extractPdf(file);
    }

    if (format === 'rtf') {
      return this.extractRtf(file);
    }

    return this.extractText(file, format);
  }

  static async extractPlainText(file: File): Promise<string> {
    const document = await this.extract(file);
    return document.markdown || document.plainText;
  }

  private static async extractDocx(file: File, format: SourceFormat): Promise<ExtractedDocument> {
    const warnings: string[] = [];
    const arrayBuffer = await file.arrayBuffer();

    try {
      const htmlResult = await mammoth.convertToHtml({ arrayBuffer });

      if (htmlResult.messages?.length > 0) {
        warnings.push(...htmlResult.messages.map(message => message.message));
      }

      const html = htmlResult.value;
      const tables = this.extractTablesFromHtml(html);
      const plainText = this.htmlToText(html);
      const markdown = this.htmlToMarkdown(html, tables);

      return this.createDocument(file, format, plainText, markdown, {
        html,
        tables,
        warnings
      });
    } catch (error) {
      warnings.push(`No se pudo convertir DOCX a HTML; usando texto crudo: ${error instanceof Error ? error.message : String(error)}`);
      const rawTextResult = await mammoth.extractRawText({ arrayBuffer });
      const plainText = this.normalizeLines(rawTextResult.value || '');
      const tables = this.extractTablesFromDelimitedText(plainText);

      return this.createDocument(file, format, plainText, plainText, {
        tables,
        warnings
      });
    }
  }

  private static async extractPdf(file: File): Promise<ExtractedDocument> {
    configurePdfjsWorker();
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer }) as any;
    const pdf = loadingTask.promise ? await loadingTask.promise : await loadingTask;
    const pages: ExtractedPage[] = [];
    const blocks: ExtractedTextBlock[] = [];
    const warnings: string[] = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const pageBlocks = textContent.items
        .filter((item: any) => item.str && item.str.trim().length > 0)
        .map((item: any) => {
          const transform = item.transform || [1, 0, 0, 1, 0, 0];
          return {
            text: item.str.trim(),
            page: pageNumber,
            x: transform[4],
            y: transform[5],
            width: item.width,
            height: item.height
          } as ExtractedTextBlock;
        });

      const pageText = this.blocksToText(pageBlocks);
      pages.push({ pageNumber, text: pageText, blocks: pageBlocks });
      blocks.push(...pageBlocks);

      if (pageText.length < 50) {
        warnings.push(`Página ${pageNumber} contiene poco texto; puede requerir OCR.`);
      }
    }

    const plainText = pages.map(page => page.text).join('\n\n');
    return this.createDocument(file, 'pdf', plainText, plainText, {
      pages,
      blocks,
      warnings
    });
  }

  private static async extractRtf(file: File): Promise<ExtractedDocument> {
    const rtf = await file.text();
    const warnings: string[] = [];

    const parsed = parseRobustRtf(rtf);
    warnings.push(...parsed.warnings);

    const rtfTables = rtfRowsToExtractedTables(
      parsed.tables.map(t => t.rows)
    );

    let plainText = parsed.plainText;
    let tables = rtfTables;

    // Fallback: intentar rtf-parser externo si texto muy corto
    if (plainText.length < 100) {
      try {
        const { extractPlainTextFromRTF } = await import('../utils/rtfUtils');
        const libText = await extractPlainTextFromRTF(rtf);
        if (libText && libText.length > plainText.length) {
          plainText = this.normalizeLines(libText);
          warnings.push('Se usó rtf-parser como respaldo para texto plano.');
        }
      } catch {
        warnings.push('rtf-parser no disponible como respaldo.');
      }
    }

    // Si no hay tablas del parser robusto, intentar delimitado por texto
    if (tables.length === 0) {
      tables = this.extractTablesFromDelimitedText(plainText);
    }

    const markdown = [plainText, ...tables.map(table => table.markdown)].filter(Boolean).join('\n\n');

    return this.createDocument(file, 'rtf', plainText, markdown, { tables, warnings });
  }

  private static async extractText(file: File, format: SourceFormat): Promise<ExtractedDocument> {
    const text = await file.text();
    const tables = this.extractTablesFromDelimitedText(text);

    return this.createDocument(file, format, text, text, { tables });
  }

  private static extractTablesFromHtml(html: string): ExtractedTable[] {
    if (typeof DOMParser === 'undefined') {
      return [];
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('table')).map((table, tableIndex) => {
      const rows = Array.from(table.querySelectorAll('tr')).map(row =>
        Array.from(row.querySelectorAll('th,td')).map(cell => ({
          text: this.normalizeWhitespace(cell.textContent || ''),
          rowSpan: Number(cell.getAttribute('rowspan') || 1),
          colSpan: Number(cell.getAttribute('colspan') || 1)
        }))
      );

      return {
        id: `table_${tableIndex + 1}`,
        rows,
        markdown: this.tableToMarkdown(rows),
        source: { tableIndex }
      };
    });
  }

  private static extractTablesFromDelimitedText(text: string): ExtractedTable[] {
    const lines = text.split(/\r?\n/);
    const tables: ExtractedTable[] = [];
    let currentRows: ExtractedTableCell[][] = [];

    const flush = () => {
      if (currentRows.length >= 2) {
        const tableIndex = tables.length;
        tables.push({
          id: `table_${tableIndex + 1}`,
          rows: currentRows,
          markdown: this.tableToMarkdown(currentRows),
          source: { tableIndex }
        });
      }
      currentRows = [];
    };

    for (const line of lines) {
      const cells = line.includes('\t')
        ? line.split('\t').map(cell => cell.trim())
        : line.split(/\s{2,}/).map(cell => cell.trim()).filter(Boolean);

      if (cells.length >= 3) {
        currentRows.push(cells.map(text => ({ text })));
      } else {
        flush();
      }
    }

    flush();
    return tables;
  }

  private static htmlToMarkdown(html: string, tables: ExtractedTable[]): string {
    let markdown = html
      .replace(/<table[\s\S]*?<\/table>/gi, '\n')
      .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

    markdown = this.normalizeLines(markdown);
    const tableMarkdown = tables.map(table => table.markdown).join('\n\n');
    return [markdown, tableMarkdown].filter(Boolean).join('\n\n');
  }

  private static htmlToText(html: string): string {
    return this.normalizeLines(
      html
        .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
    );
  }

  private static rtfToText(rtf: string): string {
    return this.normalizeLines(
      rtf
        .replace(/\\par[d]?/g, '\n')
        .replace(/\\tab/g, '\t')
        .replace(/\\'[0-9a-fA-F]{2}/g, ' ')
        .replace(/\\[a-zA-Z]+-?\d* ?/g, '')
        .replace(/[{}]/g, ' ')
    );
  }

  private static blocksToText(blocks: ExtractedTextBlock[]): string {
    const sorted = [...blocks].sort((a, b) => {
      const yDiff = (b.y || 0) - (a.y || 0);
      if (Math.abs(yDiff) > 3) return yDiff;
      return (a.x || 0) - (b.x || 0);
    });

    const lines: ExtractedTextBlock[][] = [];
    for (const block of sorted) {
      const lastLine = lines[lines.length - 1];
      const sameLine = lastLine && Math.abs((lastLine[0].y || 0) - (block.y || 0)) <= 3;
      if (sameLine) {
        lastLine.push(block);
      } else {
        lines.push([block]);
      }
    }

    return lines
      .map(line => line.sort((a, b) => (a.x || 0) - (b.x || 0)).map(block => block.text).join(' '))
      .join('\n');
  }

  private static tableToMarkdown(rows: ExtractedTableCell[][]): string {
    if (rows.length === 0) return '';

    const normalizedRows = rows.map(row => row.map(cell => this.escapeMarkdownCell(cell.text)));
    const columnCount = Math.max(...normalizedRows.map(row => row.length));
    const padRow = (row: string[]) => [...row, ...Array(Math.max(0, columnCount - row.length)).fill('')];
    const header = padRow(normalizedRows[0]);
    const separator = Array(columnCount).fill('---');
    const body = normalizedRows.slice(1).map(padRow);

    return [header, separator, ...body].map(row => `| ${row.join(' | ')} |`).join('\n');
  }

  private static createDocument(
    file: File,
    format: SourceFormat,
    plainText: string,
    markdown: string,
    extras: Partial<Pick<ExtractedDocument, 'html' | 'tables' | 'pages' | 'blocks' | 'warnings'>>
  ): ExtractedDocument {
    const tables = extras.tables || [];
    const pages = extras.pages || [];
    const blocks = extras.blocks || [];
    const warnings = extras.warnings || [];

    return {
      sourceFileName: file.name,
      sourceFormat: format,
      plainText,
      html: extras.html,
      markdown,
      tables,
      pages,
      blocks,
      warnings,
      metadata: {
        fileSize: file.size,
        extractedAt: new Date().toISOString(),
        textLength: plainText.length,
        tableCount: tables.length,
        pageCount: pages.length
      }
    };
  }

  private static getFormat(file: File): SourceFormat {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (extension === 'docx' || extension === 'doc' || extension === 'pdf' || extension === 'rtf') {
      return extension;
    }
    if (TEXT_FORMATS.has(extension)) {
      return 'txt';
    }
    return 'unknown';
  }

  private static normalizeWhitespace(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
  }

  private static normalizeLines(text: string): string {
    return text
      .split(/\r?\n/)
      .map(line => this.normalizeWhitespace(line))
      .filter(Boolean)
      .join('\n');
  }

  private static escapeMarkdownCell(text: string): string {
    return this.normalizeWhitespace(text).replace(/\|/g, '\\|');
  }
}
